import { Container, Graphics, Text, TextStyle } from 'pixi.js';

// Constants for loading screen
const BAR_WIDTH = 400;
const BAR_HEIGHT = 40;
const CORNER_RADIUS = 10;

export class LoadingScreen {
    constructor(app) {
        console.log('[LOADING] Creating new loading screen');
        this.app = app;
        this.container = new Container();
        this.container.sortableChildren = true;
        this.container.zIndex = 99999;
        this.progress = 0;
        this.status = 'Initializing...';
        
        // Create pixel art background
        this.createPixelBackground();
        
        // Create pixel art loading bar
        this.createPixelLoadingBar();
        
        // Create pixel art text elements
        this.createPixelTextElements();
        
        // Add to stage
        console.log('[LOADING] Adding container to stage');
        this.app.stage.addChild(this.container);
        console.log('[LOADING] Container added to stage, visible:', this.container.visible);
        console.log('[LOADING] Container zIndex:', this.container.zIndex);
        
        // Handle window resize
        this.resizeHandler = () => this.handleResize();
        window.addEventListener('resize', this.resizeHandler);
    }
    
    createPixelBackground() {
        this.background = new Graphics();
        
        // Create retro terminal background with scan lines
        this.background.fill({ color: 0x000000 });
        this.background.rect(0, 0, window.innerWidth, window.innerHeight);
        
        // Add scan line effect
        const scanlineHeight = 2;
        for (let y = 0; y < window.innerHeight; y += 4) {
            this.background.fill({ color: 0x00FF00, alpha: 0.03 });
            this.background.rect(0, y, window.innerWidth, scanlineHeight);
        }
        
        // Add pixel grid pattern
        const gridSize = 20;
        for (let x = 0; x < window.innerWidth; x += gridSize) {
            this.background.setStrokeStyle({ width: 1, color: 0x003300, alpha: 0.5 });
            this.background.rect(x, 0, 1, window.innerHeight);
        }
        for (let y = 0; y < window.innerHeight; y += gridSize) {
            this.background.setStrokeStyle({ width: 1, color: 0x003300, alpha: 0.5 });
            this.background.rect(0, y, window.innerWidth, 1);
        }
        
        this.container.addChild(this.background);
    }
    
    createPixelLoadingBar() {
        // Pixel art loading bar background
        this.barBackground = new Graphics();
        this.barBackground.fill({ color: 0x0F0F0F });
        this.barBackground.rect(
            (window.innerWidth - BAR_WIDTH) / 2,
            (window.innerHeight - BAR_HEIGHT) / 2,
            BAR_WIDTH,
            BAR_HEIGHT
        );
        
        // Pixel border
        this.barBackground.setStrokeStyle({ width: 2, color: 0x00FF00 });
        this.barBackground.rect(
            (window.innerWidth - BAR_WIDTH) / 2,
            (window.innerHeight - BAR_HEIGHT) / 2,
            BAR_WIDTH,
            BAR_HEIGHT
        );
        
        // Inner border
        this.barBackground.setStrokeStyle({ width: 1, color: 0x00AA00 });
        this.barBackground.rect(
            (window.innerWidth - BAR_WIDTH) / 2 + 2,
            (window.innerHeight - BAR_HEIGHT) / 2 + 2,
            BAR_WIDTH - 4,
            BAR_HEIGHT - 4
        );
        
        // Corner decorations
        const cornerSize = 8;
        this.barBackground.fill({ color: 0x00FF00 });
        // Top-left
        this.barBackground.rect(
            (window.innerWidth - BAR_WIDTH) / 2,
            (window.innerHeight - BAR_HEIGHT) / 2,
            cornerSize,
            2
        );
        this.barBackground.rect(
            (window.innerWidth - BAR_WIDTH) / 2,
            (window.innerHeight - BAR_HEIGHT) / 2,
            2,
            cornerSize
        );
        // Top-right
        this.barBackground.rect(
            (window.innerWidth - BAR_WIDTH) / 2 + BAR_WIDTH - cornerSize,
            (window.innerHeight - BAR_HEIGHT) / 2,
            cornerSize,
            2
        );
        this.barBackground.rect(
            (window.innerWidth - BAR_WIDTH) / 2 + BAR_WIDTH - 2,
            (window.innerHeight - BAR_HEIGHT) / 2,
            2,
            cornerSize
        );
        // Bottom-left
        this.barBackground.rect(
            (window.innerWidth - BAR_WIDTH) / 2,
            (window.innerHeight - BAR_HEIGHT) / 2 + BAR_HEIGHT - 2,
            cornerSize,
            2
        );
        this.barBackground.rect(
            (window.innerWidth - BAR_WIDTH) / 2,
            (window.innerHeight - BAR_HEIGHT) / 2 + BAR_HEIGHT - cornerSize,
            2,
            cornerSize
        );
        // Bottom-right
        this.barBackground.rect(
            (window.innerWidth - BAR_WIDTH) / 2 + BAR_WIDTH - cornerSize,
            (window.innerHeight - BAR_HEIGHT) / 2 + BAR_HEIGHT - 2,
            cornerSize,
            2
        );
        this.barBackground.rect(
            (window.innerWidth - BAR_WIDTH) / 2 + BAR_WIDTH - 2,
            (window.innerHeight - BAR_HEIGHT) / 2 + BAR_HEIGHT - cornerSize,
            2,
            cornerSize
        );
        
        this.container.addChild(this.barBackground);
        
        // Modern loading bar fill with pixel art
        this.barFill = new Graphics();
        this.updatePixelBarFill();
        this.container.addChild(this.barFill);
    }
    
    updatePixelBarFill() {
        this.barFill.clear();
        
        // Create pixel art fill effect
        const fillWidth = (this.progress / 100) * BAR_WIDTH;
        const fillHeight = BAR_HEIGHT - 4; // Leave border space
        
        // Main fill with pixel blocks
        const blockSize = 4;
        const startX = (window.innerWidth - BAR_WIDTH) / 2 + 2;
        const startY = (window.innerHeight - BAR_HEIGHT) / 2 + 2;
        
        for (let x = 0; x < fillWidth; x += blockSize) {
            for (let y = 0; y < fillHeight; y += blockSize) {
                // Create gradient effect with alternating colors
                const colorIndex = Math.floor((x + y) / (blockSize * 2)) % 3;
                let color = 0x00FF00; // Green
                if (colorIndex === 1) color = 0x00CC00; // Darker green
                else if (colorIndex === 2) color = 0x00AA00; // Even darker green
                
                this.barFill.fill({ color: color });
                this.barFill.rect(startX + x, startY + y, blockSize - 1, blockSize - 1);
            }
        }
        
        // Add highlight effect
        if (fillWidth > 10) {
            this.barFill.fill({ color: 0x00FF88 });
            this.barFill.rect(startX, startY, Math.min(fillWidth, 20), 4);
        }
        
        // Add glow effect for progress
        if (this.progress > 0) {
            this.barFill.fill({ color: 0x00FF00, alpha: 0.3 });
            this.barFill.rect(startX - 2, startY - 2, fillWidth + 4, fillHeight + 4);
        }
    }
    
    createPixelTextElements() {
        // Pixel art status text
        this.statusText = new Text('', new TextStyle({
            fontFamily: '"Press Start 2P", "Courier New", monospace',
            fontSize: 16,
            fontWeight: 'normal',
            fill: 0x00FFFF, // Cyan for status
            align: 'center',
            letterSpacing: 1
        }));
        this.statusText.x = window.innerWidth / 2;
        this.statusText.y = window.innerHeight / 2 - 60;
        this.statusText.anchor.set(0.5);
        this.container.addChild(this.statusText);
        
        // Pixel art progress text
        this.progressText = new Text('0%', new TextStyle({
            fontFamily: '"Press Start 2P", "Courier New", monospace',
            fontSize: 20,
            fontWeight: 'normal',
            fill: 0x00FF00, // Classic green
            align: 'center',
            letterSpacing: 2
        }));
        this.progressText.x = window.innerWidth / 2;
        this.progressText.y = window.innerHeight / 2 + 30;
        this.progressText.anchor.set(0.5);
        this.container.addChild(this.progressText);
        
        // Add pixel art game title
        this.gameTitle = new Text('ISLAND BOY', new TextStyle({
            fontFamily: '"Press Start 2P", "Courier New", monospace',
            fontSize: 48,
            fontWeight: 'normal',
            fill: 0xFFFFFF,
            align: 'center',
            letterSpacing: 4
        }));
        this.gameTitle.x = window.innerWidth / 2;
        this.gameTitle.y = window.innerHeight / 2 - 120;
        this.gameTitle.anchor.set(0.5);
        this.container.addChild(this.gameTitle);
        
        // Add subtitle
        this.subtitle = new Text('ECOSYSTEM SURVIVAL', new TextStyle({
            fontFamily: '"Press Start 2P", "Courier New", monospace',
            fontSize: 14,
            fontWeight: 'normal',
            fill: 0x888888,
            align: 'center',
            letterSpacing: 2
        }));
        this.subtitle.x = window.innerWidth / 2;
        this.subtitle.y = window.innerHeight / 2 - 85;
        this.subtitle.anchor.set(0.5);
        this.container.addChild(this.subtitle);
        
        // Add retro decoration elements
        this.createPixelDecorations();
    }
    
    createPixelDecorations() {
        // Add pixel art corner decorations
        const decorations = [];
        const decorationSize = 20;
        
        // Top-left corner
        const tlDecoration = new Graphics();
        tlDecoration.fill({ color: 0x00FF00 });
        tlDecoration.rect(20, 20, decorationSize, decorationSize);
        tlDecoration.fill({ color: 0x00AA00 });
        tlDecoration.rect(22, 22, decorationSize - 4, decorationSize - 4);
        this.container.addChild(tlDecoration);
        
        // Top-right corner
        const trDecoration = new Graphics();
        trDecoration.fill({ color: 0x00FF00 });
        trDecoration.rect(window.innerWidth - 40, 20, decorationSize, decorationSize);
        trDecoration.fill({ color: 0x00AA00 });
        trDecoration.rect(window.innerWidth - 38, 22, decorationSize - 4, decorationSize - 4);
        this.container.addChild(trDecoration);
        
        // Bottom-left corner
        const blDecoration = new Graphics();
        blDecoration.fill({ color: 0x00FF00 });
        blDecoration.rect(20, window.innerHeight - 40, decorationSize, decorationSize);
        blDecoration.fill({ color: 0x00AA00 });
        blDecoration.rect(22, window.innerHeight - 38, decorationSize - 4, decorationSize - 4);
        this.container.addChild(blDecoration);
        
        // Bottom-right corner
        const brDecoration = new Graphics();
        brDecoration.fill({ color: 0x00FF00 });
        brDecoration.rect(window.innerWidth - 40, window.innerHeight - 40, decorationSize, decorationSize);
        brDecoration.fill({ color: 0x00AA00 });
        brDecoration.rect(window.innerWidth - 38, window.innerHeight - 38, decorationSize - 4, decorationSize - 4);
        this.container.addChild(brDecoration);
    }
    
    handleResize() {
        // Update background size
        this.background.clear();
        this.background.fill({ color: 0x000000 });
        this.background.rect(0, 0, window.innerWidth, window.innerHeight);
        
        // Update bar positions
        this.barBackground.clear();
        this.barBackground.fill({ color: 0x333333 });
        this.barBackground.roundRect(
            (window.innerWidth - BAR_WIDTH) / 2,
            (window.innerHeight - BAR_HEIGHT) / 2,
            BAR_WIDTH,
            BAR_HEIGHT,
            CORNER_RADIUS
        );
        
        // Update bar fill
        this.updateProgress(this.progress);
        
        // Update text positions
        this.statusText.x = window.innerWidth / 2;
        this.statusText.y = window.innerHeight / 2 - 60;
        
        this.progressText.x = window.innerWidth / 2;
        this.progressText.y = window.innerHeight / 2 + 30;
    }
    
    updateProgress(progress, status = null) {
        this.progress = Math.min(100, Math.max(0, progress));
        if (status) this.status = status;
        
        // Update pixel art bar fill
        this.updatePixelBarFill();
        
        // Update texts with retro styling
        this.statusText.text = this.status;
        this.progressText.text = `${Math.floor(this.progress)}%`;
        
        // Add retro completion effect
        if (this.progress === 100) {
            this.progressText.style.fill = 0x00FFFF; // Cyan for completion
            this.progressText.style.fontSize = 22; // Slightly larger
        } else {
            this.progressText.style.fill = 0x00FF00; // Classic green
            this.progressText.style.fontSize = 20;
        }
    }
    
    show() {
        console.log('[LOADING] Showing loading screen');
        this.container.visible = true;
        this.container.zIndex = 99999; // Ensure it's above everything
        console.log('[LOADING] Loading screen visible:', this.container.visible);
        console.log('[LOADING] Loading screen zIndex:', this.container.zIndex);
        
        // Force a redraw
        this.app.renderer.render(this.app.stage);
    }
    
    hide() {
        console.log('[LOADING] Hiding loading screen');
        this.container.visible = false;
        console.log('[LOADING] Loading screen visible:', this.container.visible);
    }
    
    destroy() {
        console.log('[LOADING] Destroying loading screen');
        if (this.container && this.container.parent) {
            console.log('[LOADING] Removing container from parent');
            this.container.parent.removeChild(this.container);
        }
        this.container.destroy({ children: true });
        window.removeEventListener('resize', this.resizeHandler);
        console.log('[LOADING] Loading screen destroyed');
    }
} 